import React, { useState, useRef, useEffect } from "react";

// Redux
import { connect } from "react-redux";

// Libraries
import PropTypes from "prop-types";
import { Icon } from "semantic-ui-react";

// Components
import Input from "@Components/Input";

// Utils
import { fileToBase64, showPdf } from "@Utils/formats";
import { compressVideo } from "@Utils/ffmpeg";

// Styles
import styles from "./styles.module.css";

const UploadFile = ({ document, setDocument, accept, option, disabled = false }) => {
	const [dragging, setDragging] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [documents, setDocuments] = useState({});
	const inputRef = useRef(null);

	useEffect(() => {
		if (option !== "Add") {
			buildDocumentFromUrl(document);
		}
	}, [option, document]);

	const handleFileUpload = async (file) => {
		setLoading(true);
		setError(null);

		try {
			let base64 = "";

			if (file.type.startsWith("video/")) {
				base64 = await compressVideo(file);
			} else {
				base64 = await fileToBase64(file);
			}

			setDocuments({ base64, name: file.name });
			setDocument(base64);
		} catch (errors) {
			setError(error.message || "An unexpected error occurred");
		} finally {
			setLoading(false);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) handleFileUpload(file);
	};

	const handleDrop = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragging(false);
		const file = e.dataTransfer.files?.[0];
		if (file) handleFileUpload(file);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		setDragging(true);
	};

	const handleDragLeave = () => {
		setDragging(false);
	};

	const clearFile = (e) => {
		e.stopPropagation();
		setDocument(null);
		setError(null);
		setDocuments({});
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const buildDocumentFromUrl = (url) => {
		if (!url) return;
		const downloadFile = showPdf(url);
		const parts = url.split("/");
		const name = parts[parts.length - 1];
		setDocuments({ url: downloadFile, name });
	};

	// const viewFileInOtherWindow = () => {
	// 	const fileUrl = documents?.base64 || documents?.url;
	// 	if (!fileUrl) return;

	// 	if (documents?.url) {
	// 		window.open(documents.url, "_blank");
	// 		return;
	// 	}

	// 	const mime = fileUrl.match(/^data:(.*?);/i)?.[1] || "";
	// 	let content = "";

	// 	if (mime.includes("pdf")) {
	// 		content = `<iframe src="${fileUrl}" frameborder="0" style="width:100%; height:100%;" allowfullscreen></iframe>`;
	// 	} else if (mime.includes("video")) {
	// 		content = `<video src="${fileUrl}" controls autoplay style="width:100%; height:auto;"></video>`;
	// 	} else if (mime.includes("audio")) {
	// 		content = `<audio src="${fileUrl}" controls autoplay style="width:100%;"></audio>`;
	// 	} else {
	// 		content = `<p>Unsupported file type for preview.</p>`;
	// 	}

	// 	const html = `
	//     <!DOCTYPE html>
	//     <html>
	//       <head>
	//         <title>Preview - ${documents.name || "file"}</title>
	//         <meta charset="utf-8" />
	//         <style>
	//           body { margin: 0; background: #000; color: white; display: flex; align-items: center; justify-content: center; height: 100vh; }
	//           iframe, video, audio { max-width: 100%; max-height: 100%; }
	//         </style>
	//       </head>
	//       <body>
	//         ${content}
	//       </body>
	//     </html>
	//   `;

	// 	const blob = new Blob([html], { type: "text/html" });
	// 	const blobUrl = URL.createObjectURL(blob);
	// 	window.open(blobUrl, "_blank");
	// };

	const getSafeFileName = () => {
		if (documents?.name) return documents.name;
		if (typeof document === "string" && document) {
			if (document.startsWith("data:")) return "archivo";
			try {
				const u = new URL(document);
				const last = u.pathname.split("/").pop() || "archivo";
				return decodeURIComponent(last);
			} catch {
				const last = document.split("/").pop() || "archivo";
				return decodeURIComponent(last);
			}
		}
		return "archivo";
	};

	const truncate = (s, n = 30) => {
		return s && s.length > n ? `${s.slice(0, n - 3)}...` : s || "";
	};
	const safeName = getSafeFileName();

	return (
		<div
			role="button"
			tabIndex={0}
			className={`${styles.dropzone} ${dragging ? styles.dragging : ""}`}
			onDrop={handleDrop}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onClick={() => {
				return inputRef.current?.click();
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					inputRef.current?.click();
				}
			}}>
			<div className={styles.contentFile}>
				<Input ref={inputRef} type="file" accept={accept} className={styles.hiddenInput} onChange={handleFileChange} disabled={disabled} />
				{loading ? (
					<Icon className={styles.iconClass} name="spinner" loading size="huge" />
				) : (
					<Icon className={styles.iconClass} name="cloud upload" size="huge" />
				)}
				{error && <p className={styles.error}>{error}</p>}
				{document && (
					<div className={styles.fileInfo}>
						<span className={styles.filename}>📎 {truncate(safeName)}</span>
						<Icon
							name="close"
							color="red"
							className={styles.removeIcon}
							onClick={(e) => {
								e.stopPropagation();
								clearFile(e);
							}}
							title="Quitar archivo"
						/>
					</div>
				)}
			</div>
		</div>
	);
};

UploadFile.propTypes = {
	document: PropTypes.string,
	option: PropTypes.string,
	setDocument: PropTypes.func,
	accept: PropTypes.string,
	disabled: PropTypes.bool,
};

export default connect()(UploadFile);
